import * as R from 'ramda';
import {
  pure,
  withState,
  withHandlers,
  withPropsOnChange,
} from 'react-recompose';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// feature edit-report
import {
  REPORT_FIELD_DND_ID,
  omitReportFieldDndIds,
  createReportFieldDndId,
  getAvailableForReports,
  ensureReportFieldDndId,
  reorderFieldsOnDrugEnd,
  ensureReportFieldListDndIds,
  mapFieldsWithSequenceFromIndex,
} from '../../helpers';
//////////////////////////////////////////////////

const getReportFieldListProps = (props) => R.assoc(
  'availableFields',
  R.propOr(R.propOr([], 'availableFields', props), 'availableReportFields', props),
  props,
);

export const withReportFieldList = R.compose(
  withState('reportFieldList', 'setReportFieldList', []),
  withState('availableForReports', 'setAvailableForReports', []),
  withState('defaultFieldsCount', 'setDefaultFieldsCount', 5),
  withPropsOnChange(['reportFields'], (props) => {
    const {
      reportFields,
      reportFieldList,
      setReportFieldList,
      setAvailableForReports,
    } = props;

    if (G.isNilOrEmpty(reportFields)) return;

    if (G.deepEquals(reportFields, omitReportFieldDndIds(reportFieldList))) return;

    setReportFieldList(ensureReportFieldListDndIds(reportFields));
    setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), reportFields));
  }),
  withHandlers({
    handleAddReportField: (props) => () => {
      const {
        reportFieldList,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const newField = ensureReportFieldDndId({
        name: null,
        freezed: false,
        reference: false,
        collection: false,
        sequence: R.inc(R.length(reportFieldList)),
      });

      const fieldsUpdated = R.append(newField, reportFieldList);

      setReportFieldList(fieldsUpdated);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), fieldsUpdated));
    },
    handleAddAllReportField: (props) => () => {
      const {
        reportFieldList,
        availableFields,
        setReportFieldList,
        availableReportFields,
        setAvailableForReports,
      } = props;

      const fields = R.or(availableReportFields, availableFields);

      const filtered = R.filter(({ name }) => G.isNotNilAndNotEmpty(name), R.or(reportFieldList, []));

      const keys = R.compose(
        R.keys,
        R.indexBy(R.prop(GC.FIELD_NAME)),
      )(filtered);

      const length = R.length(keys);

      const availableList = R.compose(
        G.mapIndexed(({ type, value, collection = false }, index) => ({
          type,
          collection,
          guid: null,
          name: value,
          freezed: false,
          [REPORT_FIELD_DND_ID]: createReportFieldDndId(),
          sequence: R.add(R.inc(index), length),
          reference: R.equals(type, 'reference'),
        })),
        R.reject(({ value }) => R.includes(value, keys)),
      )(fields);

      const result = R.concat(filtered, availableList);

      setReportFieldList(result);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), result));
    },
    handleAddReportFields: (props) => () => {
      const {
        reportFieldList,
        defaultFieldsCount,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const newField = {
        name: null,
        freezed: false,
        reference: false,
        collection: false,
      };

      const newFields = R.times(() => ensureReportFieldDndId(newField), defaultFieldsCount);
      const fieldsUpdated = R.concat(reportFieldList, newFields);

      setReportFieldList(fieldsUpdated);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), fieldsUpdated));
    },
    handleRemoveReportField: (props) => (filterIndex) => {
      const {
        reportFieldList,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const fieldsUpdated = mapFieldsWithSequenceFromIndex(R.dissocPath([filterIndex], reportFieldList));

      setReportFieldList(fieldsUpdated);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), fieldsUpdated));
    },
    handleSelectReportFieldName: (props) => (values) => {
      const {
        reportFieldList,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const {
        fieldIndex,
        data: { type, value, uiType, searchable, origValue, collection, referenceFieldName },
      } = values;
      const reportFieldDndId = R.path([fieldIndex, REPORT_FIELD_DND_ID], reportFieldList);

      let newField = {
        origValue,
        searchable,
        name: value,
        type: uiType,
        freezed: false,
        collection: R.or(collection, false),
        [REPORT_FIELD_DND_ID]: R.or(reportFieldDndId, createReportFieldDndId()),
        reference: R.equals(type, 'reference'),
      };

      if (G.isNotNil(referenceFieldName)) {
        newField = R.assoc(GC.FIELD_REFERENCE_FIELD_NAME, referenceFieldName, newField);
      }

      const fieldsUpdated = R.assocPath([fieldIndex], newField, reportFieldList);

      setReportFieldList(fieldsUpdated);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), fieldsUpdated));
    },
    handleChangeReportFieldFrozen: (props) => ({ freezed, fieldIndex }) => {
      const {
        reportFieldList,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const fieldsUpdated = R.assocPath([fieldIndex, 'freezed'], R.not(freezed), reportFieldList);

      setReportFieldList(fieldsUpdated);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), fieldsUpdated));
    },
    handleReportFieldSortDragEnd: (props) => (result) => {
      const {
        reportFieldList,
        setReportFieldList,
        setAvailableForReports,
      } = props;

      const reorderedFields = reorderFieldsOnDrugEnd(reportFieldList, result);

      setReportFieldList(reorderedFields);

      setAvailableForReports(getAvailableForReports(getReportFieldListProps(props), reorderedFields));
    },
  }),
  pure,
);
